import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  UserPlus, Briefcase, Package, LogOut, Menu, Plus, Calculator,
  Camera, Sparkles, MapPin, Phone, Mail, Check, Copy, X, 
  FileText, ArrowRight, Clock, Calendar, Truck, Home
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import LanguageSelector from '../../components/LanguageSelector';

const SalesRepDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [todayStats, setTodayStats] = useState({ customers: 0, jobs: 0, estimates: 0 });
  
  // Create Customer Dialog
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
  });
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Quick Quote Dialog
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [quoteStep, setQuoteStep] = useState(1);
  const [quoteForm, setQuoteForm] = useState({
    customer_id: '',
    customer_name: '',
    from_address: '',
    from_city: '',
    from_postal_code: '',
    from_property_type: 'House',
    from_floor: '',
    to_address: '',
    to_city: '',
    to_postal_code: '',
    to_property_type: 'House',
    to_floor: '',
    move_date: '',
    flexible: false,
    inventory_items: [],
    notes: '',
  });
  const [estimateResult, setEstimateResult] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [recentCustomers, setRecentCustomers] = useState([]);

  useEffect(() => {
    fetchTodayStats();
    fetchRecentCustomers();
  }, []);

  const fetchTodayStats = async () => {
    try {
      // In a real implementation, this would fetch from API
      const today = new Date().toISOString().split('T')[0];
      // Placeholder - would be API call
      setTodayStats({ customers: 0, jobs: 0, estimates: 0 });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchRecentCustomers = async () => {
    try {
      const response = await api.get('/users/list');
      const customers = (response.data || []).filter(u => u.role === 'customer');
      setRecentCustomers(customers.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/mobile-login');
  };

  // Create Customer
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.first_name || !customerForm.last_name) {
      toast.error('First and last name are required');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/users/create', {
        ...customerForm,
        role: 'customer',
      });
      
      setCreatedCustomer({
        ...response.data.user,
        login_code: response.data.login_code
      });
      setTodayStats(prev => ({ ...prev, customers: prev.customers + 1 }));
      toast.success('Customer created successfully!');
      fetchRecentCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (createdCustomer?.login_code) {
      navigator.clipboard.writeText(createdCustomer.login_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
      toast.success('Code copied to clipboard!');
    }
  };

  const resetCustomerForm = () => {
    setCustomerForm({ first_name: '', last_name: '', phone: '', email: '' });
    setCreatedCustomer(null);
    setCodeCopied(false);
    setShowCreateCustomer(false);
  };

  // Quick Quote Functions
  const calculateEstimate = () => {
    // Simple estimate calculation based on inputs
    const baseRate = 150; // Base rate per hour
    const itemCount = quoteForm.inventory_items.reduce((sum, i) => sum + i.quantity, 0);
    const distanceFactor = 1.2; // Would be calculated from addresses
    
    let hours = Math.ceil(itemCount / 15) + 2; // Base hours
    if (quoteForm.from_property_type === 'Flat' && parseInt(quoteForm.from_floor) > 2) {
      hours += 1;
    }
    if (quoteForm.to_property_type === 'Flat' && parseInt(quoteForm.to_floor) > 2) {
      hours += 1;
    }
    
    const laborCost = hours * baseRate;
    const vehicleCost = 100; // Fixed vehicle cost
    const packingMaterials = itemCount * 2; // £2 per item for packing
    
    const subtotal = laborCost + vehicleCost + packingMaterials;
    const vat = subtotal * 0.2;
    const total = subtotal + vat;
    
    setEstimateResult({
      hours,
      laborCost,
      vehicleCost,
      packingMaterials,
      subtotal,
      vat,
      total,
      itemCount,
    });
    setQuoteStep(4);
  };

  const handleSaveQuote = async () => {
    setLoading(true);
    try {
      // Create the job with estimate
      const jobData = {
        customer_id: quoteForm.customer_id,
        from_address: {
          street: quoteForm.from_address,
          city: quoteForm.from_city,
          postal_code: quoteForm.from_postal_code,
        },
        to_address: {
          street: quoteForm.to_address,
          city: quoteForm.to_city,
          postal_code: quoteForm.to_postal_code,
        },
        move_out_date: quoteForm.move_date,
        property_details: {
          from_property_type: quoteForm.from_property_type,
          from_floor: quoteForm.from_floor,
          to_property_type: quoteForm.to_property_type,
          to_floor: quoteForm.to_floor,
        },
        estimate: estimateResult,
        notes: quoteForm.notes,
        status: 'pending_estimate',
      };
      
      await api.post('/jobs', jobData);
      
      setTodayStats(prev => ({ ...prev, jobs: prev.jobs + 1, estimates: prev.estimates + 1 }));
      toast.success('Quote saved and sent to customer!');
      resetQuoteForm();
    } catch (error) {
      toast.error('Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  const resetQuoteForm = () => {
    setQuoteForm({
      customer_id: '',
      customer_name: '',
      from_address: '',
      from_city: '',
      from_postal_code: '',
      from_property_type: 'House',
      from_floor: '',
      to_address: '',
      to_city: '',
      to_postal_code: '',
      to_property_type: 'House',
      to_floor: '',
      move_date: '',
      flexible: false,
      inventory_items: [],
      notes: '',
    });
    setEstimateResult(null);
    setQuoteStep(1);
    setShowQuickQuote(false);
  };

  const selectCustomerForQuote = (customer) => {
    setQuoteForm(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: `${customer.first_name} ${customer.last_name}`,
    }));
    setQuoteStep(2);
  };

  // Common inventory items for quick add
  const quickItems = [
    { name: 'Sofa (3 seater)', category: 'Living Room', quantity: 1 },
    { name: 'Double Bed', category: 'Bedroom', quantity: 1 },
    { name: 'Wardrobe', category: 'Bedroom', quantity: 1 },
    { name: 'Dining Table', category: 'Dining Room', quantity: 1 },
    { name: 'Washing Machine', category: 'Appliances', quantity: 1 },
    { name: 'Fridge Freezer', category: 'Appliances', quantity: 1 },
    { name: 'TV (Large)', category: 'Electronics', quantity: 1 },
    { name: 'Boxes (Medium)', category: 'Boxes', quantity: 10 },
  ];

  const addQuickItem = (item) => {
    const existing = quoteForm.inventory_items.find(i => i.name === item.name);
    if (existing) {
      setQuoteForm(prev => ({
        ...prev,
        inventory_items: prev.inventory_items.map(i => 
          i.name === item.name ? { ...i, quantity: i.quantity + 1 } : i
        ),
      }));
    } else {
      setQuoteForm(prev => ({
        ...prev,
        inventory_items: [...prev.inventory_items, { ...item }],
      }));
    }
  };

  const removeItem = (name) => {
    setQuoteForm(prev => ({
      ...prev,
      inventory_items: prev.inventory_items.filter(i => i.name !== name),
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="sales-rep-dashboard">
      {/* Mobile Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-8" />
              <div>
                <h1 className="text-lg font-bold gradient-text">MoveMate</h1>
                <p className="text-xs text-gray-500">Sales Rep</p>
              </div>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2">
              <Menu size={24} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t bg-white">
            <div className="p-4 space-y-2">
              <div className="mb-4">
                <p className="text-sm font-semibold">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500">Sales Representative</p>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Language</span>
                <LanguageSelector variant="compact" />
              </div>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4 pb-20">
        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-bold mb-1">Field Operations</h2>
          <p className="text-gray-600">Create customers and quotes on-site</p>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-primary">{todayStats.customers}</p>
              <p className="text-xs text-gray-500">Customers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">{todayStats.jobs}</p>
              <p className="text-xs text-gray-500">Jobs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{todayStats.estimates}</p>
              <p className="text-xs text-gray-500">Estimates</p>
            </CardContent>
          </Card>
        </div>

        {/* Primary Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Card 
            className="card-hover cursor-pointer border-2 border-primary" 
            onClick={() => setShowCreateCustomer(true)}
            data-testid="create-customer-btn"
          >
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserPlus className="text-primary" size={28} />
              </div>
              <h3 className="font-semibold">New Customer</h3>
              <p className="text-xs text-gray-500">Create account</p>
            </CardContent>
          </Card>

          <Card 
            className="card-hover cursor-pointer border-2 border-green-500" 
            onClick={() => setShowQuickQuote(true)}
            data-testid="quick-quote-btn"
          >
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calculator className="text-green-600" size={28} />
              </div>
              <h3 className="font-semibold">Quick Quote</h3>
              <p className="text-xs text-gray-500">On-site estimate</p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Actions */}
        <div className="space-y-3">
          <Card className="card-hover cursor-pointer" onClick={() => navigate('/ai-scanner')}>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Sparkles className="text-purple-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">AI Inventory Scanner</h3>
                  <p className="text-sm text-gray-600">Scan room for quick inventory</p>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer" onClick={() => navigate('/jobs/create')}>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Briefcase className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Full Job Creation</h3>
                  <p className="text-sm text-gray-600">Detailed job with all options</p>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Customers */}
        {recentCustomers.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Recent Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentCustomers.slice(0, 3).map((customer) => (
                  <div 
                    key={customer.id} 
                    className="p-3 border rounded-lg flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      selectCustomerForQuote(customer);
                      setShowQuickQuote(true);
                    }}
                  >
                    <div>
                      <p className="font-medium text-sm">{customer.first_name} {customer.last_name}</p>
                      <p className="text-xs text-gray-500">{customer.phone || customer.email}</p>
                    </div>
                    <Badge variant="outline">Code: {customer.login_code}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2 text-blue-900">Quick Tip</h3>
            <p className="text-sm text-blue-800">
              Use "Quick Quote" to create an instant estimate on-site. The customer receives 
              their quote immediately via their app!
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Create Customer Dialog */}
      <Dialog open={showCreateCustomer} onOpenChange={(open) => !open && resetCustomerForm()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="text-primary" size={24} />
              {createdCustomer ? 'Customer Created!' : 'New Customer'}
            </DialogTitle>
            <DialogDescription>
              {createdCustomer 
                ? 'Share the login code with your customer' 
                : 'Create a new customer account on-site'
              }
            </DialogDescription>
          </DialogHeader>

          {!createdCustomer ? (
            <form onSubmit={handleCreateCustomer} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={customerForm.first_name}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={customerForm.last_name}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Smith"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+44 7700 900000"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="customer@email.com"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating...' : 'Create Customer'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <Check className="mx-auto text-green-600 mb-2" size={32} />
                <p className="font-semibold text-lg">{createdCustomer.first_name} {createdCustomer.last_name}</p>
                <p className="text-sm text-gray-600">{createdCustomer.phone || createdCustomer.email}</p>
              </div>
              
              <div className="p-6 bg-primary/5 border-2 border-primary rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-2">Login Code</p>
                <p className="text-4xl font-mono font-bold tracking-widest text-primary">
                  {createdCustomer.login_code}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={copyCode}
                >
                  {codeCopied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                  {codeCopied ? 'Copied!' : 'Copy Code'}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={resetCustomerForm}>
                  Done
                </Button>
                <Button onClick={() => {
                  selectCustomerForQuote(createdCustomer);
                  resetCustomerForm();
                  setShowQuickQuote(true);
                }}>
                  <Calculator size={16} className="mr-2" />
                  Create Quote
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Quote Dialog */}
      <Dialog open={showQuickQuote} onOpenChange={(open) => !open && resetQuoteForm()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="text-green-600" size={24} />
              Quick Quote - Step {quoteStep}/4
            </DialogTitle>
            <DialogDescription>
              {quoteStep === 1 && 'Select or create customer'}
              {quoteStep === 2 && 'Enter move details'}
              {quoteStep === 3 && 'Add inventory items'}
              {quoteStep === 4 && 'Review estimate'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4">
            {/* Step 1: Customer Selection */}
            {quoteStep === 1 && (
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full h-16"
                  onClick={() => {
                    setShowQuickQuote(false);
                    setShowCreateCustomer(true);
                  }}
                >
                  <UserPlus size={20} className="mr-2" />
                  Create New Customer
                </Button>
                
                {recentCustomers.length > 0 && (
                  <div>
                    <Label className="text-sm text-gray-500">Or select existing:</Label>
                    <div className="space-y-2 mt-2">
                      {recentCustomers.map((customer) => (
                        <div 
                          key={customer.id}
                          className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex justify-between items-center"
                          onClick={() => selectCustomerForQuote(customer)}
                        >
                          <div>
                            <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                            <p className="text-xs text-gray-500">{customer.phone || customer.email}</p>
                          </div>
                          <ArrowRight size={18} className="text-gray-400" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Move Details */}
            {quoteStep === 2 && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-sm text-gray-600">Customer:</p>
                  <p className="font-semibold">{quoteForm.customer_name}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Home size={16} /> Moving From
                  </h4>
                  <AddressAutocomplete
                    value={quoteForm.from_address}
                    onChange={(val) => setQuoteForm(prev => ({ ...prev, from_address: val }))}
                    onPlaceSelect={(place) => {
                      setQuoteForm(prev => ({
                        ...prev,
                        from_address: place.street,
                        from_city: place.city,
                        from_postal_code: place.postal_code,
                      }));
                    }}
                    placeholder="Search address..."
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {['House', 'Flat', 'Office'].map(type => (
                      <Button
                        key={type}
                        type="button"
                        variant={quoteForm.from_property_type === type ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setQuoteForm(prev => ({ ...prev, from_property_type: type }))}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                  {quoteForm.from_property_type === 'Flat' && (
                    <Input
                      placeholder="Floor number"
                      value={quoteForm.from_floor}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, from_floor: e.target.value }))}
                    />
                  )}
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Truck size={16} /> Moving To
                  </h4>
                  <AddressAutocomplete
                    value={quoteForm.to_address}
                    onChange={(val) => setQuoteForm(prev => ({ ...prev, to_address: val }))}
                    onPlaceSelect={(place) => {
                      setQuoteForm(prev => ({
                        ...prev,
                        to_address: place.street,
                        to_city: place.city,
                        to_postal_code: place.postal_code,
                      }));
                    }}
                    placeholder="Search address..."
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {['House', 'Flat', 'Office'].map(type => (
                      <Button
                        key={type}
                        type="button"
                        variant={quoteForm.to_property_type === type ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setQuoteForm(prev => ({ ...prev, to_property_type: type }))}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                  {quoteForm.to_property_type === 'Flat' && (
                    <Input
                      placeholder="Floor number"
                      value={quoteForm.to_floor}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, to_floor: e.target.value }))}
                    />
                  )}
                </div>

                <div>
                  <Label>Preferred Move Date</Label>
                  <Input
                    type="date"
                    value={quoteForm.move_date}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, move_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Inventory */}
            {quoteStep === 3 && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Quick add common items:</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {quickItems.map((item) => (
                    <Button
                      key={item.name}
                      variant="outline"
                      size="sm"
                      className="h-auto py-2 px-3 justify-start text-left"
                      onClick={() => addQuickItem(item)}
                    >
                      <Plus size={14} className="mr-1 shrink-0" />
                      <span className="truncate text-xs">{item.name}</span>
                    </Button>
                  ))}
                </div>

                {quoteForm.inventory_items.length > 0 && (
                  <div>
                    <Label>Added Items ({quoteForm.inventory_items.reduce((sum, i) => sum + i.quantity, 0)} total)</Label>
                    <div className="space-y-2 mt-2">
                      {quoteForm.inventory_items.map((item, idx) => (
                        <div key={idx} className="p-2 border rounded flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium">{item.name}</p>
                            <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addQuickItem(item)}
                            >
                              <Plus size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.name)}
                            >
                              <X size={14} className="text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Special requirements, access issues, etc."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Estimate Review */}
            {quoteStep === 4 && estimateResult && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600">Estimated Total</p>
                    <p className="text-4xl font-bold text-green-600">
                      £{estimateResult.total.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">inc. VAT</p>
                  </div>
                </div>

                <Card>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Labor ({estimateResult.hours} hrs)</span>
                      <span>£{estimateResult.laborCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Vehicle</span>
                      <span>£{estimateResult.vehicleCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Packing Materials</span>
                      <span>£{estimateResult.packingMaterials.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>£{estimateResult.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>VAT (20%)</span>
                      <span>£{estimateResult.vat.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span>£{estimateResult.total.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>

                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p><strong>Customer:</strong> {quoteForm.customer_name}</p>
                  <p><strong>From:</strong> {quoteForm.from_city}</p>
                  <p><strong>To:</strong> {quoteForm.to_city}</p>
                  <p><strong>Items:</strong> {estimateResult.itemCount}</p>
                  {quoteForm.move_date && (
                    <p><strong>Date:</strong> {new Date(quoteForm.move_date).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>

          <DialogFooter className="mt-4">
            {quoteStep > 1 && quoteStep < 4 && (
              <Button variant="outline" onClick={() => setQuoteStep(quoteStep - 1)}>
                Back
              </Button>
            )}
            {quoteStep === 2 && (
              <Button onClick={() => setQuoteStep(3)}>
                Next: Add Items
              </Button>
            )}
            {quoteStep === 3 && (
              <Button onClick={calculateEstimate}>
                <Calculator size={16} className="mr-2" />
                Calculate Estimate
              </Button>
            )}
            {quoteStep === 4 && (
              <Button onClick={handleSaveQuote} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? 'Saving...' : 'Save & Send Quote'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesRepDashboard;
